import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { opponents, chat, OPPONENT_REQUEST_STATUSES } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Clock,
  Filter,
  MapPin,
  MessageCircle,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Trophy,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';


const SURFACE_META = {
  hard: 'bg-blue-100 text-blue-800 border-blue-200',
  clay: 'bg-orange-100 text-orange-800 border-orange-200',
  grass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  carpet: 'bg-purple-100 text-purple-800 border-purple-200',
  any: 'bg-zinc-100 text-zinc-700 border-zinc-200',
};

const REQUEST_STATUS_META = {
  [OPPONENT_REQUEST_STATUSES.ACCEPTED]: {
    labelKey: 'opponent.status.accepted',
    className: 'bg-lime-100 text-lime-800 border-lime-200',
  },
  [OPPONENT_REQUEST_STATUSES.DECLINED]: {
    labelKey: 'opponent.status.declined',
    className: 'bg-rose-100 text-rose-800 border-rose-200',
  },
  [OPPONENT_REQUEST_STATUSES.CANCELED]: {
    labelKey: 'opponent.status.canceled',
    className: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  },
  [OPPONENT_REQUEST_STATUSES.PENDING]: {
    labelKey: 'opponent.status.pending',
    className: 'bg-amber-100 text-amber-800 border-amber-200',
  },
};

const EmptyPanel = ({ icon: Icon, title, body }) => (
  <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-8 text-center shadow-sm">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-zinc-950 text-lime-300">
      <Icon size={28} />
    </div>
    <h3 className="mt-4 text-xl font-semibold tracking-tight text-zinc-950">{title}</h3>
    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">{body}</p>
  </div>
);

export const OpponentsPage = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [searchResults, setSearchResults] = useState([]);
  const [searchPage, setSearchPage] = useState(1);
  const [searchMeta, setSearchMeta] = useState(null);
  const [requests, setRequests] = useState({ sent: [], received: [] });
  const [filters, setFilters] = useState({ level: '', city: '', min_age: '', max_age: '' });
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [showChatModal, setShowChatModal] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestsError, setRequestsError] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  const fetchRequests = useCallback(async () => {
    setRequestsLoading(true);
    setRequestsError('');
    try {
      const response = await opponents.getRequests();
      setRequests({
        sent: Array.isArray(response.data?.sent) ? response.data.sent : [],
        received: Array.isArray(response.data?.received) ? response.data.received : [],
      });
    } catch (error) {
      setRequestsError(t('opponent.loadError'));
      toast.error(t('opponent.loadError'));
    } finally {
      setRequestsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSearch = async (page = 1) => {
    setSearchLoading(true);
    setSearchError('');
    try {
      const params = {};
      if (filters.level) params.level = filters.level;
      if (filters.city) params.city = filters.city;
      if (filters.min_age) params.min_age = parseInt(filters.min_age, 10);
      if (filters.max_age) params.max_age = parseInt(filters.max_age, 10);
      params.page = page;
      params.limit = 20;

      const response = await opponents.search(params);
      const payload = response.data || {};
      if (Array.isArray(payload)) {
        setSearchResults(payload);
        setSearchMeta(null);
      } else {
        setSearchResults(payload.items || []);
        setSearchMeta(payload.pagination || null);
      }
      setSearchPage(page);
    } catch (error) {
      setSearchError(t('opponent.searchError'));
      toast.error(t('opponent.searchError'));
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSendRequest = async (e) => {
    e.preventDefault();
    try {
      await opponents.createRequest({
        to_athlete_id: selectedOpponent.id,
        message: requestMessage,
      });
      toast.success(t('opponent.requestSent'));
      setShowRequestModal(false);
      setRequestMessage('');
      fetchRequests();
    } catch (error) {
      toast.error(t('opponent.requestError'));
    }
  };

  const handleUpdateStatus = async (requestId, status) => {
    try {
      await opponents.updateRequestStatus(requestId, status);
      toast.success(status === OPPONENT_REQUEST_STATUSES.ACCEPTED ? t('matchmaking.matchAccepted') : t('common.save'));
      fetchRequests();
    } catch (error) {
      toast.error(t('opponent.statusError'));
    }
  };

  const openChat = async (request) => {
    setCurrentRequest(request);
    setShowChatModal(true);
    try {
      const response = await chat.getMessages(request.id);
      setMessages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(t('opponent.messagesError'));
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await chat.sendMessage({
        request_id: currentRequest.id,
        message: newMessage,
      });
      const response = await chat.getMessages(currentRequest.id);
      setMessages(Array.isArray(response.data) ? response.data : []);
      setNewMessage('');
    } catch (error) {
      toast.error(t('opponent.sendMessageError'));
    }
  };

  const localeTimeString = (dateStr) => {
    const locale = i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'fr' ? 'fr-FR' : 'en-GB';
    return new Date(dateStr).toLocaleTimeString(locale);
  };

  const allRequests = useMemo(() => [...requests.received, ...requests.sent], [requests]);
  const pendingReceived = requests.received.filter((request) => request.status === OPPONENT_REQUEST_STATUSES.PENDING);
  const acceptedRequests = allRequests.filter((request) => request.status === OPPONENT_REQUEST_STATUSES.ACCEPTED);
  const suggestedOpponents = searchResults.slice(0, 3);
  const activeFilters = [filters.level, filters.city, filters.min_age, filters.max_age].filter(Boolean).length;

  const summaryCards = [
    {
      label: t('opponent.searchResults'),
      value: searchMeta?.total ?? searchResults.length,
      detail: searchResults.length ? t('opponent.athletesReturned') : t('opponent.runSearchToDiscover'),
      Icon: Users,
      color: 'text-zinc-900',
    },
    {
      label: t('opponent.pendingRequests'),
      value: pendingReceived.length,
      detail: t('opponent.awaitingResponse'),
      Icon: Clock,
      color: pendingReceived.length ? 'text-amber-700' : 'text-zinc-600',
    },
    {
      label: t('opponent.status.accepted'),
      value: acceptedRequests.length,
      detail: t('opponent.chatEnabledRequests'),
      Icon: CheckCircle2,
      color: acceptedRequests.length ? 'text-lime-700' : 'text-zinc-600',
    },
    {
      label: t('opponent.filters'),
      value: activeFilters,
      detail: t('opponent.activeSearchFields'),
      Icon: Filter,
      color: 'text-blue-700',
    },
  ];

  const statusBadge = (status) => {
    const meta = REQUEST_STATUS_META[status] || REQUEST_STATUS_META[OPPONENT_REQUEST_STATUSES.PENDING];
    return (
      <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${meta.className}`}>
        {t(meta.labelKey)}
      </span>
    );
  };

  const renderOpponentCard = (opponent) => (
    <article
      key={opponent.id}
      className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-lime-300 hover:shadow-xl hover:shadow-zinc-200/70"
      data-testid={`opponent-card-${opponent.id}`}
    >
      <div className="h-1.5 bg-gradient-to-r from-lime-300 to-emerald-500" />
      <div className="p-5">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-semibold tracking-tight text-zinc-950">{opponent.name}</h3>
            <p className="mt-2 flex items-center gap-2 text-sm text-zinc-500">
              <MapPin size={15} />
              {opponent.city || t('opponent.locationNotPublished')}
            </p>
          </div>
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-lime-200 bg-lime-50 text-lime-700">
            <Trophy size={19} />
          </div>
        </div>

        <div className="grid gap-2">
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs font-semibold text-zinc-700">
              {opponent.level || t('opponent.levelNotSet')}
            </span>
            <span className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-semibold text-zinc-600">
              {t('opponent.rankValue', { rank: opponent.rank || '-' })}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full border border-lime-200 bg-lime-50 px-3 py-1 text-xs font-semibold text-lime-800">
              {t('opponent.compatibilityNotScored')}
            </span>
            <span className="rounded-full border px-3 py-1 text-xs font-semibold bg-zinc-100 text-zinc-700 border-zinc-200">
              {t('opponent.availabilityNotPublished')}
            </span>
            <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${SURFACE_META.any}`}>
              {t('opponent.surfaceValue', { surface: t('opponent.surfaces.any') })}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            setSelectedOpponent(opponent);
            setShowRequestModal(true);
          }}
          className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-zinc-950 px-4 text-sm font-semibold text-white transition hover:bg-zinc-800"
          data-testid={`send-request-${opponent.id}`}
        >
          <UserPlus size={17} />
          {t('opponent.sendRequest')}
        </button>
      </div>
    </article>
  );

  const renderRequestCard = (request, direction) => (
    <div key={request.id} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {statusBadge(request.status)}
            <span className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs font-semibold text-zinc-600">
              {t(`opponent.directions.${direction}`)}
            </span>
          </div>
          <p className="font-semibold text-zinc-950">{request.message}</p>
          <p className="mt-2 text-sm text-zinc-500">
            {request.created_at ? localeTimeString(request.created_at) : t('opponent.timeNotAvailable')}
          </p>
        </div>

        <div className="flex shrink-0 gap-2">
          {direction === 'received' && request.status === OPPONENT_REQUEST_STATUSES.PENDING && (
            <>
              <button
                type="button"
                onClick={() => handleUpdateStatus(request.id, OPPONENT_REQUEST_STATUSES.ACCEPTED)}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-lime-500 text-zinc-950 transition hover:bg-lime-400"
                data-testid={`accept-request-${request.id}`}
                aria-label={t('opponent.acceptRequestAria')}
              >
                <Check size={18} />
              </button>
              <button
                type="button"
                onClick={() => handleUpdateStatus(request.id, OPPONENT_REQUEST_STATUSES.DECLINED)}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-rose-600 text-white transition hover:bg-rose-500"
                data-testid={`reject-request-${request.id}`}
                aria-label={t('opponent.declineRequestAria')}
              >
                <X size={18} />
              </button>
            </>
          )}
          {request.status === OPPONENT_REQUEST_STATUSES.ACCEPTED && (
            <button
              type="button"
              onClick={() => openChat(request)}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-zinc-200 px-4 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50"
              data-testid={`open-chat-${request.id}`}
            >
              <MessageCircle size={17} />
              {t('opponent.chat')}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f6f7f4] px-4 pb-24 pt-4 lg:px-8 lg:pb-10 lg:pt-8" data-testid="opponents-page">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="relative overflow-hidden rounded-3xl bg-zinc-950 p-6 text-white shadow-2xl shadow-zinc-300/60 md:p-8">
          <div className="pointer-events-none absolute inset-0 opacity-25">
            <div className="absolute -right-20 top-6 h-60 w-60 rounded-full border border-lime-300" />
            <div className="absolute bottom-8 left-8 h-px w-72 rotate-6 bg-lime-300" />
            <div className="absolute bottom-20 left-1/2 h-px w-80 -rotate-12 bg-white/40" />
          </div>
          <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-end">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-lime-200">
                <span className="h-2 w-2 rounded-full bg-lime-300" />
                {t('opponent.heroKicker')}
              </div>
              <h1 className="max-w-3xl text-4xl font-semibold tracking-tight md:text-5xl">{t('opponent.title')}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 md:text-base">
                {t('opponent.heroBody')}
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-300">{t('opponent.requestQueue')}</span>
                <ShieldCheck className="text-lime-300" size={21} />
              </div>
              <div className="text-4xl font-semibold tracking-tight text-white">{pendingReceived.length}</div>
              <p className="mt-2 text-sm text-zinc-300">{t('opponent.requestQueueBody')}</p>
            </div>
          </div>
        </section>

        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map(({ label, value, detail, Icon, color }) => (
            <div key={label} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-500">{label}</span>
                <Icon className={color} size={20} />
              </div>
              <div className={`text-3xl font-semibold tracking-tight ${color}`}>{value}</div>
              <p className="mt-1 truncate text-sm text-zinc-500">{detail}</p>
            </div>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-5">
            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-zinc-700">
                <Search size={18} />
                {t('opponent.searchOpponents')}
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_120px_120px_auto]">
                <input
                  type="text"
                  placeholder={t('opponent.level')}
                  className="min-h-11 rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  value={filters.level}
                  onChange={(e) => setFilters({ ...filters, level: e.target.value })}
                  data-testid="filter-level"
                />
                <input
                  type="text"
                  placeholder={t('opponent.city')}
                  className="min-h-11 rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                />
                <input
                  type="number"
                  placeholder={t('opponent.minAgePlaceholder')}
                  className="min-h-11 rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  value={filters.min_age}
                  onChange={(e) => setFilters({ ...filters, min_age: e.target.value })}
                />
                <input
                  type="number"
                  placeholder={t('opponent.maxAgePlaceholder')}
                  className="min-h-11 rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  value={filters.max_age}
                  onChange={(e) => setFilters({ ...filters, max_age: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => handleSearch(1)}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-zinc-950 px-5 text-sm font-semibold text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-zinc-300"
                  disabled={searchLoading}
                  data-testid="search-button"
                >
                  <Search size={17} />
                  {searchLoading ? t('common.loading') : t('common.search')}
                </button>
              </div>
            </div>

            {searchLoading && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {[0, 1, 2, 3].map((item) => (
                  <div key={item} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                    <div className="h-1.5 rounded bg-zinc-100" />
                    <div className="mt-5 h-5 w-40 animate-pulse rounded bg-zinc-200" />
                    <div className="mt-4 h-20 animate-pulse rounded-xl bg-zinc-100" />
                  </div>
                ))}
              </div>
            )}

            {!searchLoading && searchError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center">
                <AlertTriangle className="mx-auto text-rose-600" size={36} />
                <h2 className="mt-4 text-xl font-semibold tracking-tight text-rose-950">{t('opponent.searchLoadError')}</h2>
                <p className="mt-2 text-sm text-rose-700">{searchError}</p>
              </div>
            )}

            {!searchLoading && !searchError && searchResults.length === 0 && (
              <EmptyPanel
                icon={Users}
                title={t('opponent.searchTitle')}
                body={t('opponent.searchEmptyBody')}
              />
            )}

            {!searchLoading && !searchError && searchResults.length > 0 && (
              <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-semibold tracking-tight text-zinc-950">{t('opponent.searchResults')}</h2>
                    <p className="mt-1 text-sm text-zinc-500">{t('opponent.searchResultsBody')}</p>
                  </div>
                  {searchMeta && (
                    <span className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs font-semibold text-zinc-600">
                      {t('opponent.pageLabel', { page: searchMeta.page, total: searchMeta.totalPages || 1 })}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {searchResults.map(renderOpponentCard)}
                </div>
                {searchMeta && (
                  <div className="mt-5 flex items-center justify-end gap-3">
                    <button
                      type="button"
                      className="inline-flex min-h-11 items-center justify-center rounded-full border border-zinc-200 px-5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() => handleSearch(Math.max(1, searchPage - 1))}
                      disabled={!searchMeta.hasPrev}
                    >
                      {t('common.prev')}
                    </button>
                    <button
                      type="button"
                      className="inline-flex min-h-11 items-center justify-center rounded-full border border-zinc-200 px-5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() => handleSearch(searchPage + 1)}
                      disabled={!searchMeta.hasNext}
                    >
                      {t('common.next')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {requestsLoading && (
              <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                <div className="h-5 w-40 animate-pulse rounded bg-zinc-200" />
                <div className="mt-5 space-y-3">
                  {[0, 1].map((item) => (
                    <div key={item} className="h-20 animate-pulse rounded-xl bg-zinc-100" />
                  ))}
                </div>
              </div>
            )}

            {!requestsLoading && requestsError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-900">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 shrink-0 text-rose-600" size={22} />
                  <div>
                    <h2 className="font-semibold">{t('opponent.requestsLoadError')}</h2>
                    <p className="mt-1 text-sm text-rose-700">{requestsError}</p>
                  </div>
                </div>
              </div>
            )}

            {!requestsLoading && !requestsError && (
              <div className="grid gap-5">
                <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                  <h2 className="mb-4 text-xl font-semibold tracking-tight text-zinc-950">{t('opponent.receivedRequests')}</h2>
                  {requests.received.length > 0 ? (
                    <div className="space-y-3">
                      {requests.received.map((request) => renderRequestCard(request, 'received'))}
                    </div>
                  ) : (
                    <p className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-4 text-sm leading-6 text-zinc-500">
                      {t('opponent.noIncomingRequests')}
                    </p>
                  )}
                </div>

                <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                  <h2 className="mb-4 text-xl font-semibold tracking-tight text-zinc-950">{t('opponent.sentRequests')}</h2>
                  {requests.sent.length > 0 ? (
                    <div className="space-y-3">
                      {requests.sent.map((request) => renderRequestCard(request, 'sent'))}
                    </div>
                  ) : (
                    <p className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-4 text-sm leading-6 text-zinc-500">
                      {t('opponent.noSentRequests')}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-5">
            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('opponent.suggestedOpponents')}</h2>
                  <p className="text-sm text-zinc-500">{t('opponent.suggestedBody')}</p>
                </div>
                <Sparkles className="text-lime-700" size={22} />
              </div>
              {suggestedOpponents.length > 0 ? (
                <div className="space-y-3">
                  {suggestedOpponents.map((opponent) => (
                    <button
                      key={opponent.id}
                      type="button"
                      onClick={() => {
                        setSelectedOpponent(opponent);
                        setShowRequestModal(true);
                      }}
                      className="w-full rounded-xl border border-zinc-100 bg-zinc-50 p-4 text-left transition hover:border-lime-300 hover:bg-lime-50"
                    >
                      <p className="font-semibold text-zinc-950">{opponent.name}</p>
                      <p className="mt-1 text-sm text-zinc-500">{[opponent.city, opponent.level].filter(Boolean).join(' - ') || t('opponent.profileBasicsOnly')}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-4 text-sm leading-6 text-zinc-500">
                  {t('opponent.suggestedEmptyBody')}
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('opponent.profileDetail')}</h2>
                  <p className="text-sm text-zinc-500">{t('opponent.profileDetailBody')}</p>
                </div>
                <Users className="text-zinc-700" size={22} />
              </div>
              {selectedOpponent ? (
                <div className="space-y-3">
                  <div className="rounded-xl border border-lime-100 bg-lime-50 p-4">
                    <p className="text-xl font-semibold tracking-tight text-zinc-950">{selectedOpponent.name}</p>
                    <p className="mt-1 text-sm text-lime-800">{selectedOpponent.city || t('opponent.locationNotPublished')}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-3">
                      <p className="text-xs uppercase tracking-wide text-zinc-500">{t('opponent.level')}</p>
                      <p className="mt-1 font-semibold text-zinc-950">{selectedOpponent.level || '-'}</p>
                    </div>
                    <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-3">
                      <p className="text-xs uppercase tracking-wide text-zinc-500">{t('opponent.rank')}</p>
                      <p className="mt-1 font-semibold text-zinc-950">{selectedOpponent.rank || '-'}</p>
                    </div>
                  </div>
                  <p className="rounded-xl border border-zinc-100 bg-zinc-50 p-4 text-sm leading-6 text-zinc-500">
                    {t('opponent.profileUnavailableBody')}
                  </p>
                </div>
              ) : (
                <p className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-4 text-sm leading-6 text-zinc-500">
                  {t('opponent.selectOpponentBody')}
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('opponent.requestHealth')}</h2>
                  <p className="text-sm text-zinc-500">{t('opponent.requestHealthBody')}</p>
                </div>
                <ShieldCheck className="text-lime-700" size={22} />
              </div>
              <div className="space-y-3">
                {Object.values(OPPONENT_REQUEST_STATUSES).map((status) => {
                  const count = allRequests.filter((request) => request.status === status).length;
                  return (
                    <div key={status} className="flex items-center justify-between rounded-xl border border-zinc-100 bg-zinc-50 px-4 py-3">
                      {statusBadge(status)}
                      <span className="text-sm font-semibold text-zinc-950">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>
        </section>
      </div>

      {showRequestModal && selectedOpponent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/70 p-4" data-testid="request-modal">
          <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-zinc-100 bg-white/95 px-6 py-5 backdrop-blur">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-lime-700">{t('opponent.matchRequest')}</p>
                <h2 className="text-2xl font-semibold tracking-tight text-zinc-950">{t('opponent.sendRequest')}</h2>
                <p className="mt-1 text-sm text-zinc-500">{selectedOpponent.name}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowRequestModal(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-zinc-200 text-zinc-600 transition hover:bg-zinc-50"
                aria-label={t('opponent.closeRequestFormAria')}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSendRequest} className="space-y-5 p-6">
              <div>
                <label className="mb-2 block text-sm font-semibold text-zinc-700">{t('opponent.message')}</label>
                <textarea
                  className="min-h-32 w-full rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  rows={4}
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  required
                  data-testid="input-message"
                />
              </div>
              <div className="flex flex-col-reverse gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setShowRequestModal(false)}
                  className="inline-flex min-h-11 flex-1 items-center justify-center rounded-full border border-zinc-200 px-5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full bg-zinc-950 px-5 text-sm font-semibold text-white transition hover:bg-zinc-800"
                  data-testid="submit-request"
                >
                  <Send size={18} />
                  {t('opponent.sendRequest')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showChatModal && currentRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/70 p-4" data-testid="chat-modal">
          <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-zinc-100 px-6 py-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-lime-700">{t('opponent.acceptedRequest')}</p>
                <h2 className="text-2xl font-semibold tracking-tight text-zinc-950">{t('opponent.chat')}</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowChatModal(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-zinc-200 text-zinc-600 transition hover:bg-zinc-50"
                aria-label={t('opponent.closeChatAria')}
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-6">
              {messages.length > 0 ? (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`max-w-[82%] rounded-2xl p-3 ${
                      msg.from_user_id === user.id
                        ? 'ml-auto bg-zinc-950 text-white'
                        : 'bg-zinc-100 text-zinc-950'
                    }`}
                  >
                    <p>{msg.message}</p>
                    <p className="mt-1 text-xs opacity-70">{localeTimeString(msg.created_at)}</p>
                  </div>
                ))
              ) : (
                <p className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-4 text-sm leading-6 text-zinc-500">
                  {t('opponent.noMessagesYet')}
                </p>
              )}
            </div>
            <form onSubmit={handleSendMessage} className="flex gap-2 border-t border-zinc-100 p-4">
              <input
                type="text"
                className="min-h-11 flex-1 rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                data-testid="input-chat-message"
              />
              <button
                type="submit"
                className="inline-flex min-h-11 items-center justify-center rounded-full bg-zinc-950 px-5 text-sm font-semibold text-white transition hover:bg-zinc-800"
                data-testid="send-message"
                aria-label={t('opponent.sendMessage')}
              >
                <Send size={20} />
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
